/* eslint-disable no-underscore-dangle,graphql/template-strings */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { adopt } from 'react-adopt';
import gql from 'graphql-tag';
import {
  defaultNumberOfRows as DEFAULT_NUMBER_OF_ROWS,
  reviewStatus,
} from 'config';
import { Query, Mutation } from 'components/common/Query';
import { PageInfo } from 'components/common/Paginator';
import RouterContext from 'helpers/routerContext';
import qs, { getQueryString } from 'helpers/queryStrings';
import ReviewList from './List';

export const REVIEW_USER_FIELDS = `
  id
  fullname
  email
  phone
  rate
  reviewCount
`;

const REVIEW_BASE_FIELDS = `
  id
  rate
  comment
  status
  matchedRules
  reportCount
  publishedOn
  reviewedOn
  deletedOn
  createdOn
  updatedOn
  lastReportedOn
  reviewer {
    ${REVIEW_USER_FIELDS}
  }
  reviewee {
    ${REVIEW_USER_FIELDS}
  }
`;

export const REVIEW_LIST_FIELDS = `
  ${REVIEW_BASE_FIELDS}
  reports {
    id
    reason
    status
  }
`;

export const REVIEW_DETAILS_FIELDS = `
  ${REVIEW_BASE_FIELDS}
  reviewedBy {
    ${REVIEW_USER_FIELDS}
  }
  reports {
    id
    reason
    status
    comment
    createdOn
    reporter {
      id
      fullname
    }
  }
`;

export const ADMIN_REVIEWS_QUERY = gql`
  query AdminReviewsList(
    $first: Int
    $after: String
    $last: Int
    $before: String
    $status: [FilterByReviewStatus]
    $reviewer: ID
    $reviewee: ID
    $moderator: ID
    $rate: Float
    $hasReports: Boolean
    $reportCountGte: Float
    $reportCountLte: Float
    $createdOnGte: DateTime
    $createdOnLte: DateTime
    $updatedOnGte: DateTime
    $updatedOnLte: DateTime
    $publishedOnGte: DateTime
    $publishedOnLte: DateTime
    $reviewedOnGte: DateTime
    $reviewedOnLte: DateTime
    $lastReportedOnGte: DateTime
    $lastReportedOnLte: DateTime
  ) {
    adminReviews(
      first: $first
      after: $after
      last: $last
      before: $before
      status: $status
      reviewer: $reviewer
      reviewee: $reviewee
      moderator: $moderator
      rate: $rate
      hasReports: $hasReports
      reportCountGte: $reportCountGte
      reportCountLte: $reportCountLte
      createdOnGte: $createdOnGte
      createdOnLte: $createdOnLte
      updatedOnGte: $updatedOnGte
      updatedOnLte: $updatedOnLte
      publishedOnGte: $publishedOnGte
      publishedOnLte: $publishedOnLte
      reviewedOnGte: $reviewedOnGte
      reviewedOnLte: $reviewedOnLte
      lastReportedOnGte: $lastReportedOnGte
      lastReportedOnLte: $lastReportedOnLte
    ) @connection(key: "admin_reviews") {
      pageInfo {
        ...PaginatorPageInfo
      }
      edges {
        node {
          ${REVIEW_LIST_FIELDS}
        }
      }
    }
  }
  ${PageInfo}
`;

export const PUBLISH_REVIEWS_MUTATION = gql`
  mutation($ids: [ID]!) {
    publishReviews(input: { ids: $ids }) {
      successIds
      errors {
        field
        messages
      }
    }
  }
`;

export const UNPUBLISH_REVIEWS_MUTATION = gql`
  mutation($ids: [ID]!) {
    unpublishReviews(input: { ids: $ids }) {
      successIds
      errors {
        field
        messages
      }
    }
  }
`;

export const REPROCESS_REVIEWS_MUTATION = gql`
  mutation($ids: [ID]!) {
    reprocessReviews(input: { ids: $ids }) {
      successIds
      errors {
        field
        messages
      }
    }
  }
`;

export const SEND_REVIEWS_TO_REVIEW_MUTATION = gql`
  mutation($ids: [ID]!) {
    sendReviewsToReview(input: { ids: $ids }) {
      successIds
      errors {
        field
        messages
      }
    }
  }
`;

export const DELETE_REVIEWS_MUTATION = gql`
  mutation($ids: [ID]!) {
    deleteReviews(input: { ids: $ids }) {
      successIds
      errors {
        field
        messages
      }
    }
  }
`;

export const defaultFilters = {
  status: undefined,
  reviewer: undefined,
  reviewee: undefined,
  moderator: undefined,
  rate: undefined,
  hasReports: undefined,
  reportCountGte: undefined,
  reportCountLte: undefined,
  createdOnGte: undefined,
  createdOnLte: undefined,
  updatedOnGte: undefined,
  updatedOnLte: undefined,
  publishedOnGte: undefined,
  publishedOnLte: undefined,
  reviewedOnGte: undefined,
  reviewedOnLte: undefined,
  lastReportedOnGte: undefined,
  lastReportedOnLte: undefined,
};

const numericFields = ['rate', 'reportCountGte', 'reportCountLte'];
const dateFields = [
  'createdOnGte',
  'createdOnLte',
  'updatedOnGte',
  'updatedOnLte',
  'publishedOnGte',
  'publishedOnLte',
  'reviewedOnGte',
  'reviewedOnLte',
  'lastReportedOnGte',
  'lastReportedOnLte',
];

const parseNumericFilter = value => {
  if (value === undefined) return undefined;

  const normalizedValue = typeof value === 'string' ? value.trim() : value;
  if (normalizedValue === '') return undefined;

  const parsedValue = Number(normalizedValue);
  return Number.isFinite(parsedValue) ? parsedValue : undefined;
};

const cleanFilters = filters =>
  Object.keys(filters).reduce((result, field) => {
    const value = filters[field];
    if (value === '' || value === null || value === 'null') {
      return { ...result, [field]: undefined };
    }
    if (numericFields.includes(field)) {
      return {
        ...result,
        [field]: parseNumericFilter(value),
      };
    }
    if (field === 'status' && typeof value === 'string') {
      return { ...result, status: [value] };
    }
    return { ...result, [field]: value };
  }, {});

const serializeFilters = filters =>
  Object.keys(filters).reduce((result, field) => {
    const value = filters[field];
    if (dateFields.includes(field)) {
      return { ...result, [field]: value === null ? 'null' : value };
    }
    return { ...result, [field]: value };
  }, {});

export class ReviewListContainer extends Component {
  static contextType = RouterContext;

  constructor(props) {
    super(props);
    const { initialFilters } = props;
    const {
      direction,
      cursor,
      numberOfRowsSelected,
      ...initFilters
    } = initialFilters;
    this.state = {
      numberOfRowsSelected:
        parseInt(numberOfRowsSelected, 10) || DEFAULT_NUMBER_OF_ROWS,
      filters: cleanFilters({ ...defaultFilters, ...initFilters }),
      pagination: { direction: direction || null, cursor: cursor || undefined },
      itemProcessing: [],
      failedIds: [],
      showNotification: false,
    };
    this.Compose = adopt({
      mutatePublish: this.publishReviews,
      mutateUnpublish: this.unpublishReviews,
      mutateReprocess: this.reprocessReviews,
      mutateSendToReview: this.sendReviewsToReview,
      mutateDelete: this.deleteReviews,
      queryReviews: this.listReviews,
    });
    this.refetchReviews = () => Promise.resolve();
  }

  getVariables = () => {
    const { filters, pagination, numberOfRowsSelected } = this.state;
    const paginationVariables =
      !pagination.direction || pagination.direction === 'NEXT'
        ? {
            first: numberOfRowsSelected,
            after: pagination.cursor,
          }
        : {
            last: numberOfRowsSelected,
            before: pagination.cursor,
          };

    return {
      ...paginationVariables,
      ...filters,
    };
  };

  listReviews = ({ render }) => (
    <Query
      query={ADMIN_REVIEWS_QUERY}
      variables={this.getVariables()}
      notifyOnNetworkStatusChange
      fetchPolicy="cache-and-network"
    >
      {({ data, loading, refetch }) => {
        this.refetchReviews = refetch;
        return render({ data, loading });
      }}
    </Query>
  );

  publishReviews = ({ render }) => (
    <Mutation mutation={PUBLISH_REVIEWS_MUTATION}>
      {handlePublish => render({ handlePublish })}
    </Mutation>
  );

  unpublishReviews = ({ render }) => (
    <Mutation mutation={UNPUBLISH_REVIEWS_MUTATION}>
      {handleUnpublish => render({ handleUnpublish })}
    </Mutation>
  );

  reprocessReviews = ({ render }) => (
    <Mutation mutation={REPROCESS_REVIEWS_MUTATION}>
      {handleReprocess => render({ handleReprocess })}
    </Mutation>
  );

  sendReviewsToReview = ({ render }) => (
    <Mutation mutation={SEND_REVIEWS_TO_REVIEW_MUTATION}>
      {handleSendToReview => render({ handleSendToReview })}
    </Mutation>
  );

  deleteReviews = ({ render }) => (
    <Mutation mutation={DELETE_REVIEWS_MUTATION}>
      {handleDelete => render({ handleDelete })}
    </Mutation>
  );

  runMutation = (items, mutate, responseField) => {
    this.setState(({ itemProcessing }) => ({
      itemProcessing: [...itemProcessing, ...items],
      failedIds: [],
      showNotification: false,
    }));
    return mutate({ variables: { ids: items } })
      .then(({ data }) => {
        const successIds = (data[responseField].successIds || []).map(
          id => `${id}`,
        );
        const failedIds = items.filter(item => !successIds.includes(`${item}`));
        this.setState(({ itemProcessing }) => ({
          itemProcessing: itemProcessing.filter(item => !items.includes(item)),
          failedIds,
          showNotification: failedIds.length > 0,
        }));
        return this.refetchReviews().catch(() => undefined);
      })
      .catch(() => {
        this.setState(({ itemProcessing }) => ({
          itemProcessing: itemProcessing.filter(item => !items.includes(item)),
          failedIds: items,
          showNotification: true,
        }));
      });
  };

  handleFilter = filters => {
    const history = this.context;
    this.setState({
      filters: cleanFilters({ ...defaultFilters, ...filters }),
      pagination: { direction: null, cursor: undefined },
    });
    const currentLocation = history.location.pathname;
    const { numberOfRowsSelected } = getQueryString(history);
    const stringified = qs.stringify({
      ...serializeFilters(filters),
      numberOfRowsSelected,
      direction: undefined,
      cursor: undefined,
    });
    history.push({ pathname: currentLocation, search: stringified });
  };

  handleRestartFilters = () => {
    const history = this.context;
    this.setState({
      filters: { ...defaultFilters },
      pagination: { direction: null, cursor: undefined },
    });
    const currentLocation = history.location.pathname;
    const { numberOfRowsSelected } = getQueryString(history);
    const stringified = qs.stringify({ numberOfRowsSelected });
    history.push({ pathname: currentLocation, search: stringified });
  };

  handleChangeNumberOfRows = selectedItem => {
    const history = this.context;
    this.setState({
      numberOfRowsSelected: selectedItem,
      pagination: { direction: null, cursor: undefined },
    });
    const currentLocation = history.location.pathname;
    const parsed = getQueryString(history);
    const stringified = qs.stringify({
      ...parsed,
      numberOfRowsSelected: selectedItem,
      direction: undefined,
      cursor: undefined,
    });
    history.push({ pathname: currentLocation, search: stringified });
  };

  handleChangePage = ({ direction, cursor }) => {
    const history = this.context;
    this.setState({ pagination: { direction, cursor } });
    const currentLocation = history.location.pathname;
    const parsed = getQueryString(history);
    const stringified = qs.stringify({
      ...parsed,
      direction,
      cursor,
    });
    history.push({ pathname: currentLocation, search: stringified });
  };

  handleRefresh = () => {
    this.refetchReviews();
  };

  handleCloseNotification = () => {
    this.setState({ showNotification: false });
  };

  render() {
    const {
      numberOfRowsSelected,
      filters,
      pagination,
      itemProcessing,
      failedIds,
      showNotification,
    } = this.state;

    return (
      <this.Compose>
        {({
          mutatePublish: { handlePublish },
          mutateUnpublish: { handleUnpublish },
          mutateReprocess: { handleReprocess },
          mutateSendToReview: { handleSendToReview },
          mutateDelete: { handleDelete },
          queryReviews: { data, loading },
        }) => (
          <ReviewList
            data={data}
            publishReviews={items =>
              this.runMutation(items, handlePublish, 'publishReviews')
            }
            unpublishReviews={items =>
              this.runMutation(items, handleUnpublish, 'unpublishReviews')
            }
            reprocessReviews={items =>
              this.runMutation(items, handleReprocess, 'reprocessReviews')
            }
            sendReviewsToReview={items =>
              this.runMutation(items, handleSendToReview, 'sendReviewsToReview')
            }
            deleteReviews={items =>
              this.runMutation(items, handleDelete, 'deleteReviews')
            }
            numberOfRowsSelected={numberOfRowsSelected}
            paginationDirection={pagination.direction}
            changePage={this.handleChangePage}
            changeNumberOfRows={this.handleChangeNumberOfRows}
            appliedFilter={filters}
            filter={this.handleFilter}
            restartFilters={this.handleRestartFilters}
            refresh={this.handleRefresh}
            loading={Object.keys(data || {}).length === 0}
            fullLoading={loading}
            itemProcessing={itemProcessing}
            failedIds={failedIds}
            showNotification={showNotification}
            closeNotification={this.handleCloseNotification}
          />
        )}
      </this.Compose>
    );
  }
}

ReviewListContainer.propTypes = {
  initialFilters: PropTypes.shape({}).isRequired,
};

export { cleanFilters, serializeFilters, reviewStatus };
export default ReviewListContainer;
